import { useState, type ReactNode } from 'react'
import './Header.css'

export interface HeaderProps {
  /** Usually a routed link wrapping `<Logo/>`, e.g. `<Link to="/"><Logo/></Link>`. */
  logo: ReactNode
  /**
   * Nav links, supplied by the consumer so real routing (e.g. react-router
   * `NavLink`) stays outside the design system. Called with `close`, which
   * should be wired to each link's `onClick` so picking a link also closes
   * the mobile menu. Give the active link the `is-active` class name.
   */
  nav: (close: () => void) => ReactNode
  cta?: ReactNode
}

function BurgerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="4" y1="4" x2="20" y2="20" />
      <line x1="20" y1="4" x2="4" y2="20" />
    </svg>
  )
}

/**
 * Sticky site header: logo, nav (collapsing to a full-screen mobile menu
 * below 1120px), and an optional CTA. Router-agnostic by design — like the
 * rest of this library, routed links are supplied by the consumer as
 * `ReactNode` rather than baked in, so no `react-router-dom` dependency here.
 */
export function Header({ logo, nav, cta }: HeaderProps) {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  return (
    <header className="azoya-header">
      <div className="azoya-header-row">
        <span className="azoya-header-logo">{logo}</span>
        <nav className={`azoya-header-nav${open ? ' azoya-header-nav--open' : ''}`}>
          {nav(close)}
          <button type="button" className="azoya-header-nav-close" onClick={close} aria-label="Fermer le menu">
            <CloseIcon />
          </button>
        </nav>
        {cta && <span className="azoya-header-cta">{cta}</span>}
        <button type="button" className="azoya-header-burger" onClick={() => setOpen(true)} aria-label="Ouvrir le menu">
          <BurgerIcon />
        </button>
      </div>
    </header>
  )
}
